import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Globe, Smartphone, ArrowLeft, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { AppSelector } from "./AppSelector";
import { RuleTypeSelector } from "./RuleTypeSelector";
import { useCreateNetworkRule } from "@/hooks/useNetworkRules";
import { useToast } from "@/hooks/use-toast";

interface SelectedApp {
  bundleId: string;
  appName: string;
  iconUrl?: string;
  trackId?: number | null;
  id?: string; // Add app ID
}

interface AddRuleSheetProps {
  children?: React.ReactNode;
  isOpen?: boolean;
  onClose?: () => void;
  levelId?: string;
}

type RuleType = "system" | "app" | "";

export const AddRuleSheet = ({ children, isOpen: externalIsOpen, onClose, levelId }: AddRuleSheetProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [step, setStep] = useState<"type-selection" | "form">("type-selection");
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Use external control if provided, otherwise use internal state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = externalIsOpen !== undefined ? (open: boolean) => {
    if (!open && onClose) onClose();
  } : setInternalIsOpen;
  
  // Form state
  const [selectedRuleType, setSelectedRuleType] = useState<RuleType>("");
  const [ruleName, setRuleName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedApps, setSelectedApps] = useState<SelectedApp[]>([]);
  const [bundleId, setBundleId] = useState("");
  
  // Alternative Bundle ID state
  const [useSecondaryBundleId, setUseSecondaryBundleId] = useState(false);
  const [secondaryBundleId, setSecondaryBundleId] = useState("");
  
  const { toast } = useToast();
  const createRuleMutation = useCreateNetworkRule();

  const resetForm = () => {
    setStep("type-selection");
    setSelectedRuleType("");
    setRuleName("");
    setDescription("WHAT:\n\nWHY:");
    setSelectedApps([]);
    setBundleId("");
    setUseSecondaryBundleId(false);
    setSecondaryBundleId("");
  };

  const handleRuleTypeSelect = (type: RuleType) => {
    setSelectedRuleType(type);
    setStep("form");
  };

  const handleBack = () => {
    if (step === "form") {
      setStep("type-selection");
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!ruleName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a name for the rule.",
        variant: "destructive",
      });
      return;
    }


    if (selectedRuleType === "app" && selectedApps.length === 0) {
      toast({
        title: "App Required",
        description: "Please select an app.",
        variant: "destructive",
      });
      return;
    }

    if (selectedRuleType === "app" && useSecondaryBundleId && !secondaryBundleId.trim()) {
      toast({
        title: "Alternative Bundle ID Required",
        description: "Please enter an alternative bundle ID or disable the option.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Debug logging to trace WhatsApp bug
      console.log('=== AddRuleSheet handleSubmit ===');
      console.log('selectedRuleType:', selectedRuleType);
      console.log('selectedApps:', selectedApps);
      
      let apps: SelectedApp[] = [];
      let ruleTypeForAPI: "General" | "App Specific" = "General";
      let ruleBundleId: string | undefined = undefined;
      let ruleSecondaryBundleId: string | undefined = undefined;
      let ruleUseSecondaryBundleId: boolean = false;
      let parentAppId: string | undefined = undefined;

      if (selectedRuleType === "app") {
        apps = selectedApps;
        ruleTypeForAPI = "App Specific";
        ruleSecondaryBundleId = useSecondaryBundleId ? secondaryBundleId.trim() : undefined;
        ruleUseSecondaryBundleId = useSecondaryBundleId;
        // For app-specific rules, use the first selected app as the parent
        if (selectedApps.length > 0 && selectedApps[0].id) {
          parentAppId = selectedApps[0].id;
        }
      } else if (selectedRuleType === "system") {
        // System-wide rules can optionally have a bundle_id
        ruleTypeForAPI = "General";
        ruleBundleId = bundleId.trim() || undefined;
      }

      // Handle bundle ID logic - if alternative bundle ID is enabled, use that as the main bundle ID
      let mainBundleId = ruleBundleId || selectedApps[0]?.bundleId || '';
      if (ruleUseSecondaryBundleId && ruleSecondaryBundleId) {
        mainBundleId = ruleSecondaryBundleId;
      }

      const newRule = await createRuleMutation.mutateAsync({
        name: ruleName.trim(),
        description: description.trim() || undefined,
        type: ruleTypeForAPI,
        apps,
        is_app_specific: selectedRuleType === "app",
        bundle_id: ruleBundleId,
        secondary_bundle_id: ruleSecondaryBundleId,
        use_secondary_bundle_id: ruleUseSecondaryBundleId,
        urls: [], // Start with empty URLs
        ruleType: "socket", // Default to socket
        parent_app_id: parentAppId,
      });

      resetForm();
      setIsOpen(false);

      // Check if we got a valid ID back
      const ruleId = newRule?.id || newRule?._id;
      
      if (ruleId) {
        toast({
          title: "Rule Created",
          description: "Network rule has been created successfully. Navigating to URL configuration...",
        });
        
        // Give time for the Convex query to refresh before navigating
        setTimeout(() => {
          const newParams = new URLSearchParams(searchParams);
          newParams.set('rule', ruleId);
          newParams.set('tab', 'urls');
          setSearchParams(newParams);
        }, 500);
      } else if (newRule?.pendingMatch) {
        // Backend didn't return ID - user needs to select from list
        toast({
          title: "Rule Created",
          description: "Rule was created. Please select it from the list to add URLs.",
        });
      } else {
        toast({
          title: "Rule Created",
          description: "Network rule has been created successfully.",
        });
      }
    } catch (error) {
      console.error('Error creating rule:', error);
      toast({
        title: "Error",
        description: "Failed to create rule. Please try again.",
        variant: "destructive",
      });
    }
  };

  const trigger = children;

  const getRuleTypeTitle = () => {
    switch (selectedRuleType) {
      case "system": return "System-Wide Rule";
      case "app": return "App-Specific Rule";
      default: return "Create Network Rule";
    }
  };

  const getRuleTypeDescription = () => {
    switch (selectedRuleType) {
      case "system": return "This rule will apply to all traffic system-wide.";
      case "app": return "This rule will apply to specific selected applications.";
      default: return "Create a new network rule to control WebKit and Socket traffic.";
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => {
      if (open) {
        // Reset form when opening to prevent stale state (e.g., WhatsApp bug)
        resetForm();
        console.log('AddRuleSheet opened - form state reset');
      }
      setIsOpen(open);
      if (!open) resetForm();
    }}>
      <SheetTrigger asChild>
        {trigger}
      </SheetTrigger>
      <SheetContent className="w-[500px] sm:max-w-[500px] flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <div className="flex items-center space-x-2">
            {step === "form" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="p-0 h-auto"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <SheetTitle>{getRuleTypeTitle()}</SheetTitle>
          </div>
          <SheetDescription>
            {getRuleTypeDescription()}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-6">
          {step === "type-selection" && (
            <div className="space-y-4">
              <Label className="text-base font-medium">Select Rule Type</Label>
              <div className="grid gap-3">
                {/* System Rule */}
                <button
                  type="button"
                  className="p-4 border rounded-lg text-left transition-colors hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={() => handleRuleTypeSelect("system")}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <Globe className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-gray-900">System-Wide</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Apply network rules across the entire system for all applications and traffic.
                  </p>
                </button>

                {/* App Rule */}
                <button
                  type="button"
                  className="p-4 border rounded-lg text-left transition-colors hover:border-purple-300 hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  onClick={() => handleRuleTypeSelect("app")}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <Smartphone className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-gray-900">App-Specific</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Apply network rules to specific applications selected from your catalog.
                  </p>
                </button>
              </div>
            </div>
          )}

          {step === "form" && (
            <>
              {/* App Selection for App-Specific Rules - moved to top */}
              {selectedRuleType === "app" && (
                <div className="space-y-3">
                  <Label>Select App *</Label>
                  <AppSelector
                    selectedApps={selectedApps}
                    onAppsChange={setSelectedApps}
                    maxSelection={1}
                  />
                  {selectedApps.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedApps.map((app) => (
                        <Badge key={app.bundleId} variant="secondary" className="flex items-center space-x-1">
                          <span>{app.appName}</span>
                          <button
                            type="button"
                            onClick={() => setSelectedApps([])}
                            className="ml-1 text-gray-500 hover:text-gray-700"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Rule Name */}
              <div className="space-y-2">
                <Label htmlFor="rule-name">Rule Name *</Label>
                <p className="text-xs text-muted-foreground font-medium">
                  Format: &lt;Traffic Type&gt;
                </p>
                <Input
                  id="rule-name"
                  placeholder="Media CDN"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="WHAT: Blocks WhatsApp media traffic (images, videos, voice notes).

WHY: Prevents media consumption while allowing core messaging."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={5}
                />
              </div>

              {/* Bundle ID for System-Wide Rules */}
              {selectedRuleType === "system" && (
                <div className="space-y-2">
                  <Label htmlFor="bundle-id">Bundle ID (Optional)</Label>
                  <Input
                    id="bundle-id"
                    placeholder="e.g., com.app.feature.login"
                    value={bundleId}
                    onChange={(e) => setBundleId(e.target.value)}
                  />
                  <p className="text-sm text-gray-500">
                    Optionally target a specific bundle identifier within this system-wide rule
                  </p>
                </div>
              )}

              {/* Alternative Bundle ID for App-Specific Rules */}
              {selectedRuleType === "app" && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Switch
                      checked={useSecondaryBundleId}
                      onCheckedChange={setUseSecondaryBundleId}
                    />
                    <Label className="text-sm font-medium">
                      Use Alternative Bundle ID
                    </Label>
                  </div>

                  {useSecondaryBundleId && (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="secondary-bundle-id">
                          Alternative Bundle ID *
                        </Label>
                        <Input
                          id="secondary-bundle-id"
                          value={secondaryBundleId}
                          onChange={(e) => setSecondaryBundleId(e.target.value)}
                          placeholder="e.g., com.example.actualbundle"
                        />
                      </div>

                      <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-800">
                          <strong>Important:</strong> Even though this rule was found within the selected app, 
                          it's really targeting a different bundle ID. If enabled, this rule will apply everywhere, 
                          not only within the main app. If you think it will appear in other apps as well, 
                          consider adding it to system-wide rules instead.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </>
          )}
        </div>

        <SheetFooter className="flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          {step === "form" && (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={createRuleMutation.isPending}
            >
              {createRuleMutation.isPending ? "Creating..." : "Create Rule"}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
